import {
  Add,
  Apartment,
  AccountBalance,
  DeleteOutline,
  Gavel,
  LocationCity,
} from '@mui/icons-material';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { Delete } from '@mui/icons-material';
import React, { useState } from 'react';
import {
  useCreateCourtAdminUnitAssignment,
  useDeleteCourtAdminUnitAssignment,
  useDeleteCourt,
  useDeleteAuthority,
  useCreateCourtMunicipalityAssignment,
  useDeleteCourtMunicipalityAssignment,
  useCreateCourtPostcodeAssignment,
  useDeleteCourtPostcodeAssignment,
} from '../../Aemter/hooks';
import { PostcodeSearch } from '../../Clients/AddressTab/PostcodeSearch';
import { ApiError, customFetcher } from '../../hooks/useFetch';
import { useAlert } from '../../context/AlertContext';
import type { ICourt } from '../../Interfaces/ICourt';
import type { IAuthority } from '../../Interfaces/IAuthority';
import type { ICourtRole } from '../../Interfaces/ICourtRole';
import type { ICourtAdminstrativeUnitAssignment } from '../../Interfaces/ICourtAdminstrativeUnitAssignment';
import type { ICourtMunicipalityAssignment } from '../../Interfaces/ICourtMunicipalityAssignment';
import type { ICourtPostcodeAssignment } from '../../Interfaces/ICourtPostcodeAssignment';
import type { IAdminstrativeUnit } from '../../Interfaces/IAdminstrativeUnit';
import type { IMunicipality } from '../../Interfaces/IMunicipality';
import type { IPostcode } from '../../Interfaces/IPostcode';
import type { IFederalState } from '../../Interfaces/IAemter';
import type { AemterNavigate } from './aemterNav';
import { EditCourt } from './Edits/EditCourt';
import { EditAuthority } from './Edits/EditAuthority';

export type DetailMode = 'gemarkung' | 'court' | 'authority';

interface IProps {
  mode: DetailMode;
  entityId: number;
  federalStates: IFederalState[];
  courts: ICourt[];
  authorities: IAuthority[];
  administrativeUnits: IAdminstrativeUnit[];
  /** Alle (aktiven) Rollen — für Gemeinde-/PLZ-Zuweisung; Grundbuchamt wird intern gefiltert. */
  courtRoles: ICourtRole[];
  /** Gemeinden (für die Gemeinde-Zuweisung per Suche). */
  municipalities: IMunicipality[];
  /** Gemarkung-Zuordnungen (Grundbuchamt). */
  assignments: ICourtAdminstrativeUnitAssignment[];
  /** Gemeinde-/PLZ-Zuordnungen dieser Stelle — read-only Überblick. */
  courtMunicipalityAssignments?: ICourtMunicipalityAssignment[];
  courtPostcodeAssignments?: ICourtPostcodeAssignment[];
  onNavigate?: AemterNavigate;
  onAnyChange: () => void;
  /** Nach dem Löschen — Auswahl im Elternteil zurücksetzen. */
  onDeleted?: () => void;
}

interface Dependencies {
  gemarkungen?: number;
  gemeinden?: number;
  plz?: number;
  mandanten?: number;
  grundbuchblaetter?: number;
  immobilien?: number;
}

const extractErrorText = (e: unknown, fallback: string): string => {
  if (
    e instanceof ApiError &&
    e.body &&
    typeof e.body === 'object' &&
    'fehler' in e.body
  ) {
    const f = (e.body as { fehler?: unknown }).fehler;
    if (typeof f === 'string' && f) return f;
  }
  return fallback;
};

/**
 * Detail-Pane für ein Gericht bzw. eine Behörde — zeigt, wo die Stelle zuständig
 * ist:
 *   • Gericht: Gemarkungen (als Grundbuchamt, editierbare Aufzählung) sowie
 *     read-only die Gemeinde-/PLZ-Zuordnungen mit ihren Rollen.
 *   • Behörde: nur die Gemeinde-/PLZ-Zuordnungen (Gemarkung/Grundbuchamt ist für
 *     Behörden nicht relevant).
 *
 * Die Rolle „Grundbuchamt" wird bei den Gemarkungen NICHT je Eintrag wiederholt
 * — sie ist durch die Beziehung Gericht↔Gemarkung eindeutig.
 */
export const CourtAssignmentDetailPane: React.FC<IProps> = ({
  mode,
  entityId,
  federalStates,
  courts,
  authorities,
  administrativeUnits,
  courtRoles,
  municipalities,
  assignments,
  courtMunicipalityAssignments,
  courtPostcodeAssignments,
  onNavigate,
  onAnyChange,
  onDeleted,
}) => {
  const { showAlert } = useAlert();
  const { createCourtAdminUnitAssignment, isPendingCreateAdminUnitAssignment } =
    useCreateCourtAdminUnitAssignment();
  const { deleteCourtAdminstrativeUnitAssignment, isPending: isPendingDelete } =
    useDeleteCourtAdminUnitAssignment();
  const { createCourtMunicipalityAssignment } =
    useCreateCourtMunicipalityAssignment();
  const { deleteCourtMunicipalityAssignment } =
    useDeleteCourtMunicipalityAssignment();
  const { createCourtPostcodeAssignment } = useCreateCourtPostcodeAssignment();
  const { deleteCourtPostcodeAssignment } = useDeleteCourtPostcodeAssignment();
  const { deleteCourt, isPendingDeleteCourt } = useDeleteCourt();
  const { deleteAuthority, isPendingDeleteAuthority } = useDeleteAuthority();

  const [addingGemarkung, setAddingGemarkung] = useState(false);
  const [gemarkungToAdd, setGemarkungToAdd] =
    useState<IAdminstrativeUnit | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deps, setDeps] = useState<Dependencies | null>(null);
  const [loadingDeps, setLoadingDeps] = useState(false);
  // Gemeinde-/PLZ-Zuweisung (Anker + Rolle)
  const [addingMuni, setAddingMuni] = useState(false);
  const [muniToAdd, setMuniToAdd] = useState<IMunicipality | null>(null);
  const [muniRole, setMuniRole] = useState<number | ''>('');
  const [addingPlz, setAddingPlz] = useState(false);
  const [plzToAdd, setPlzToAdd] = useState<IPostcode | null>(null);
  const [plzRole, setPlzRole] = useState<number | ''>('');

  const isCourt = mode !== 'authority';
  const court = isCourt ? courts.find(c => c.idCourt === entityId) : undefined;
  const authority = !isCourt
    ? authorities.find(a => a.idAuthority === entityId)
    : undefined;

  const fsName = (idFederalState?: number) =>
    federalStates.find(f => f.idFederalState === idFederalState)
      ?.FederalState ?? '';

  const ownFederalState = isCourt
    ? court?.idFederalState
    : authority?.idFederalState;
  const entityName = isCourt
    ? (court?.Court ?? '–')
    : (authority?.Authority ?? '–');

  const grundbuchamtRole = courtRoles.find(r => r.CourtRole === 'Grundbuchamt');

  // --- Gemarkungen (Grundbuchamt) dieses Gerichts ---
  const gemarkungName = (id: number) =>
    administrativeUnits.find(u => u.idAdministrativeUnit === id)
      ?.AdministrativeUnit ?? `Gemarkung ${id}`;

  const myGemarkungAssignments = isCourt
    ? assignments.filter(
        a => a.idCourt === entityId && a.activeAssignment !== false
      )
    : [];
  const assignedGemarkungIds = new Set(
    myGemarkungAssignments.map(a => a.idAdministrativeUnit)
  );

  const addableGemarkungen = administrativeUnits
    .filter(
      u =>
        u.idFederalState === ownFederalState &&
        !assignedGemarkungIds.has(u.idAdministrativeUnit)
    )
    .sort((a, b) => a.AdministrativeUnit.localeCompare(b.AdministrativeUnit));

  const handleAddGemarkung = async () => {
    if (!gemarkungToAdd || !grundbuchamtRole || ownFederalState === undefined)
      return;
    try {
      await createCourtAdminUnitAssignment({
        idAssignment: -1,
        idCourt: entityId,
        idAuthority: null,
        idAdministrativeUnit: gemarkungToAdd.idAdministrativeUnit,
        idCourtRole: grundbuchamtRole.idCourtRole,
        idFederalState: ownFederalState,
        activeAssignment: true,
      });
      showAlert({ text: 'Gemarkung zugeordnet' });
      setGemarkungToAdd(null);
      setAddingGemarkung(false);
      onAnyChange();
    } catch (e) {
      console.error(e);
      showAlert({
        severity: 'error',
        text: extractErrorText(e, 'Fehler beim Speichern'),
      });
    }
  };

  const handleRemoveGemarkung = async (
    a: ICourtAdminstrativeUnitAssignment
  ) => {
    try {
      await deleteCourtAdminstrativeUnitAssignment(a);
      showAlert({ text: 'Zuordnung entfernt' });
      onAnyChange();
    } catch (e) {
      console.error(e);
      showAlert({ severity: 'error', text: 'Fehler beim Entfernen' });
    }
  };

  // --- Gemeinde-/PLZ-Zuordnungen (editierbar) ---
  const officeMatches = (
    idCourt?: number | null,
    idAuthority?: number | null
  ) => (isCourt ? idCourt === entityId : idAuthority === entityId);

  // Wählbare Rollen dieser Stelle (Kategorie passend, ohne Grundbuchamt).
  const officeRoles = courtRoles.filter(
    r =>
      r.activeRole !== false &&
      r.RoleCategory === (isCourt ? 'court' : 'authority') &&
      r.CourtRole !== 'Grundbuchamt'
  );

  const addMuniAssignment = async () => {
    if (!muniToAdd?.idMunicipality || typeof muniRole !== 'number') return;
    try {
      await createCourtMunicipalityAssignment({
        idAssignment: -1,
        idCourt: isCourt ? entityId : null,
        idAuthority: isCourt ? null : entityId,
        idMunicipality: muniToAdd.idMunicipality,
        idCourtRole: muniRole,
        activeAssignment: true,
      });
      showAlert({ text: 'Gemeinde zugeordnet' });
      setMuniToAdd(null);
      setMuniRole('');
      setAddingMuni(false);
      onAnyChange();
    } catch (e) {
      console.error(e);
      showAlert({
        severity: 'error',
        text: extractErrorText(e, 'Fehler beim Speichern'),
      });
    }
  };
  const removeMuniAssignment = async (idAssignment: number) => {
    try {
      await deleteCourtMunicipalityAssignment({
        idAssignment,
        idMunicipality: 0,
        idCourtRole: 0,
      });
      showAlert({ text: 'Zuordnung entfernt' });
      onAnyChange();
    } catch (e) {
      console.error(e);
      showAlert({ severity: 'error', text: 'Fehler beim Entfernen' });
    }
  };

  const addPlzAssignment = async () => {
    if (!plzToAdd?.idPostcode || typeof plzRole !== 'number') return;
    try {
      await createCourtPostcodeAssignment({
        idAssignment: -1,
        idCourt: isCourt ? entityId : null,
        idAuthority: isCourt ? null : entityId,
        idPostcode: plzToAdd.idPostcode,
        idCourtRole: plzRole,
        activeAssignment: true,
      });
      showAlert({ text: 'PLZ zugeordnet' });
      setPlzToAdd(null);
      setPlzRole('');
      setAddingPlz(false);
      onAnyChange();
    } catch (e) {
      console.error(e);
      showAlert({
        severity: 'error',
        text: extractErrorText(e, 'Fehler beim Speichern'),
      });
    }
  };
  const removePlzAssignment = async (idAssignment: number) => {
    try {
      await deleteCourtPostcodeAssignment({
        idAssignment,
        idPostcode: 0,
        idCourtRole: 0,
      });
      showAlert({ text: 'Zuordnung entfernt' });
      onAnyChange();
    } catch (e) {
      console.error(e);
      showAlert({ severity: 'error', text: 'Fehler beim Entfernen' });
    }
  };

  // Nach ROLLE gruppieren (wie bei den Gemarkungen): je Rolle eine flache Liste
  // der zugeordneten Gemeinden bzw. PLZ.
  type MuniItem = { idAssignment: number; id: number; name: string };
  type PlzItem = {
    idAssignment: number;
    id: number;
    postcode: string;
    city: string;
  };
  const muniByRole = new Map<
    number,
    { roleLabel: string; items: MuniItem[] }
  >();
  (courtMunicipalityAssignments ?? [])
    .filter(
      a =>
        a.activeAssignment !== false && officeMatches(a.idCourt, a.idAuthority)
    )
    .forEach(a => {
      const g = muniByRole.get(a.idCourtRole) ?? {
        roleLabel: a.CourtRole ?? `Rolle ${a.idCourtRole}`,
        items: [],
      };
      g.items.push({
        idAssignment: a.idAssignment,
        id: a.idMunicipality,
        name: a.Municipality ?? `Gemeinde ${a.idMunicipality}`,
      });
      muniByRole.set(a.idCourtRole, g);
    });
  const plzByRole = new Map<number, { roleLabel: string; items: PlzItem[] }>();
  (courtPostcodeAssignments ?? [])
    .filter(
      a =>
        a.activeAssignment !== false && officeMatches(a.idCourt, a.idAuthority)
    )
    .forEach(a => {
      const g = plzByRole.get(a.idCourtRole) ?? {
        roleLabel: a.CourtRole ?? `Rolle ${a.idCourtRole}`,
        items: [],
      };
      g.items.push({
        idAssignment: a.idAssignment,
        id: a.idPostcode,
        postcode: a.Postcode ?? '',
        city: a.City ?? '',
      });
      plzByRole.set(a.idCourtRole, g);
    });
  const muniRoleGroups = Array.from(muniByRole.values())
    .map(g => ({
      ...g,
      items: g.items.slice().sort((a, b) => a.name.localeCompare(b.name)),
    }))
    .sort((a, b) => a.roleLabel.localeCompare(b.roleLabel));
  const plzRoleGroups = Array.from(plzByRole.values())
    .map(g => ({
      ...g,
      items: g.items
        .slice()
        .sort((a, b) => a.postcode.localeCompare(b.postcode)),
    }))
    .sort((a, b) => a.roleLabel.localeCompare(b.roleLabel));
  const muniCount = muniRoleGroups.reduce((n, g) => n + g.items.length, 0);
  const plzCount = plzRoleGroups.reduce((n, g) => n + g.items.length, 0);

  // --- Löschen (Soft-Delete) mit Abhängigkeits-Warnung ---
  const openDeleteConfirm = async () => {
    setConfirmOpen(true);
    setLoadingDeps(true);
    setDeps(null);
    try {
      const endpoint = isCourt
        ? `/court/dependencies/${entityId}`
        : `/authority/dependencies/${entityId}`;
      const d = await customFetcher({ endpoint });
      setDeps((d ?? {}) as Dependencies);
    } catch (e) {
      console.error(e);
      setDeps({});
    } finally {
      setLoadingDeps(false);
    }
  };

  const handleDelete = async () => {
    try {
      if (isCourt) await deleteCourt(entityId);
      else await deleteAuthority(entityId);
      showAlert({
        text: isCourt
          ? 'Gericht gelöscht (deaktiviert)'
          : 'Behörde gelöscht (deaktiviert)',
      });
      setConfirmOpen(false);
      onAnyChange();
      onDeleted?.();
    } catch (e) {
      console.error(e);
      showAlert({ severity: 'error', text: 'Fehler beim Löschen' });
    }
  };
  const isDeleting = isPendingDeleteCourt || isPendingDeleteAuthority;

  const depRows: { label: string; value: number }[] = deps
    ? [
        { label: 'Gemarkungen (Grundbuchamt)', value: deps.gemarkungen ?? 0 },
        { label: 'Gemeinden', value: deps.gemeinden ?? 0 },
        { label: 'Postleitzahlen', value: deps.plz ?? 0 },
        { label: 'Mandanten', value: deps.mandanten ?? 0 },
        { label: 'Grundbuchblätter', value: deps.grundbuchblaetter ?? 0 },
        { label: 'Immobilien', value: deps.immobilien ?? 0 },
      ].filter(r => r.value > 0)
    : [];

  // Add-Zeile (Anker-Autocomplete/Suche + Rolle) als kleiner Helfer.
  const roleSelect = (value: number | '', onChange: (v: number) => void) => (
    <TextField
      select
      size='small'
      label='Rolle'
      value={value}
      onChange={e => onChange(Number(e.target.value))}
      sx={{ minWidth: 170 }}
      helperText={officeRoles.length === 0 ? 'Keine aktive Rolle' : undefined}
    >
      {officeRoles.map(r => (
        <MenuItem key={r.idCourtRole} value={r.idCourtRole}>
          {r.CourtRole}
        </MenuItem>
      ))}
    </TextField>
  );

  return (
    <Box>
      {/* Stammdaten */}
      <Card variant='outlined' sx={{ mb: 2 }}>
        <CardContent>
          <Stack direction='row' spacing={1} alignItems='center' sx={{ mb: 1 }}>
            {isCourt ? (
              <Gavel color='primary' />
            ) : (
              <AccountBalance color='primary' />
            )}
            <Box sx={{ flex: 1 }}>
              <Typography variant='h6'>{entityName}</Typography>
              <Typography variant='caption' color='text.secondary'>
                {fsName(ownFederalState)}
              </Typography>
            </Box>
            <Button
              size='small'
              color='error'
              startIcon={<Delete />}
              onClick={() => void openDeleteConfirm()}
            >
              {isCourt ? 'Gericht löschen' : 'Behörde löschen'}
            </Button>
          </Stack>
          {isCourt && court && (
            <EditCourt
              key={`court-${court.idCourt}`}
              currentCourt={court}
              refetchCourts={onAnyChange}
            />
          )}
          {!isCourt && authority && (
            <EditAuthority
              key={`authority-${authority.idAuthority}`}
              currentAuthority={authority}
              refetchAuthorities={onAnyChange}
            />
          )}
        </CardContent>
      </Card>

      {/* Gemeinde-/PLZ-Zuordnungen — zuerst, nach Rolle gruppiert */}
      <Card variant='outlined' sx={{ mb: isCourt ? 2 : 0 }}>
        <CardContent>
          {/* Zugeordnete Gemeinden */}
          <Box sx={{ mb: 2 }}>
            <Stack
              direction='row'
              justifyContent='space-between'
              alignItems='center'
              sx={{ mb: 1 }}
            >
              <Typography variant='subtitle1' fontWeight={600}>
                Zugeordnete Gemeinden
                {muniCount > 0 ? ` (${muniCount})` : ''}
              </Typography>
              {!addingMuni && (
                <Button
                  size='small'
                  variant='outlined'
                  startIcon={<Add />}
                  onClick={() => setAddingMuni(true)}
                  disabled={officeRoles.length === 0}
                >
                  Gemeinde hinzufügen
                </Button>
              )}
            </Stack>
            {muniRoleGroups.length === 0 ? (
              <Typography variant='body2' color='text.secondary'>
                Noch keine Gemeinde-Zuordnung.
              </Typography>
            ) : (
              <Stack spacing={1.5}>
                {muniRoleGroups.map(g => (
                  <Box key={g.roleLabel}>
                    <Typography variant='caption' color='text.secondary'>
                      Als {g.roleLabel}:
                    </Typography>
                    <Box
                      sx={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: 0.5,
                        mt: 0.5,
                      }}
                    >
                      {g.items.map(it => (
                        <Chip
                          key={it.idAssignment}
                          icon={<LocationCity />}
                          size='small'
                          variant='outlined'
                          clickable={!!onNavigate}
                          onClick={
                            onNavigate
                              ? () =>
                                  onNavigate({
                                    kind: 'municipality',
                                    id: it.id,
                                  })
                              : undefined
                          }
                          onDelete={() =>
                            void removeMuniAssignment(it.idAssignment)
                          }
                          deleteIcon={
                            <Tooltip title='Zuordnung entfernen'>
                              <DeleteOutline />
                            </Tooltip>
                          }
                          label={it.name}
                        />
                      ))}
                    </Box>
                  </Box>
                ))}
              </Stack>
            )}
            {/* Gemeinde hinzufügen (Rolle frei wählbar) */}
            {addingMuni && (
              <Stack
                direction='row'
                spacing={1}
                alignItems='flex-start'
                sx={{ mt: 1, flexWrap: 'wrap' }}
              >
                <Autocomplete
                  sx={{ flex: 1, minWidth: 220 }}
                  size='small'
                  openOnFocus
                  options={municipalities}
                  value={muniToAdd}
                  onChange={(_, v) => setMuniToAdd(v)}
                  getOptionLabel={o =>
                    o.AGS ? `${o.Municipality} (AGS ${o.AGS})` : o.Municipality
                  }
                  isOptionEqualToValue={(o, v) =>
                    o.idMunicipality === v.idMunicipality
                  }
                  filterOptions={(opts, state) => {
                    const q = state.inputValue.trim().toLowerCase();
                    if (!q) return opts.slice(0, 50);
                    return opts
                      .filter(o => o.Municipality.toLowerCase().includes(q))
                      .slice(0, 50);
                  }}
                  renderInput={params => (
                    <TextField
                      {...params}
                      autoFocus
                      label='Gemeinde'
                      placeholder='Gemeinde suchen…'
                    />
                  )}
                />
                {roleSelect(muniRole, setMuniRole)}
                <Button
                  variant='contained'
                  onClick={() => void addMuniAssignment()}
                  disabled={!muniToAdd || typeof muniRole !== 'number'}
                >
                  Zuordnen
                </Button>
                <Button
                  onClick={() => {
                    setAddingMuni(false);
                    setMuniToAdd(null);
                    setMuniRole('');
                  }}
                >
                  Abbrechen
                </Button>
              </Stack>
            )}
          </Box>

          {/* Zugeordnete Postleitzahlen */}
          <Box>
            <Stack
              direction='row'
              justifyContent='space-between'
              alignItems='center'
              sx={{ mb: 1 }}
            >
              <Typography variant='subtitle1' fontWeight={600}>
                Zugeordnete Postleitzahlen
                {plzCount > 0 ? ` (${plzCount})` : ''}
              </Typography>
              {!addingPlz && (
                <Button
                  size='small'
                  variant='outlined'
                  startIcon={<Add />}
                  onClick={() => setAddingPlz(true)}
                  disabled={officeRoles.length === 0}
                >
                  PLZ hinzufügen
                </Button>
              )}
            </Stack>
            {plzRoleGroups.length === 0 ? (
              <Typography variant='body2' color='text.secondary'>
                Noch keine PLZ-Zuordnung.
              </Typography>
            ) : (
              <Stack spacing={1.5}>
                {plzRoleGroups.map(g => (
                  <Box key={g.roleLabel}>
                    <Typography variant='caption' color='text.secondary'>
                      Als {g.roleLabel}:
                    </Typography>
                    <Box
                      sx={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: 0.5,
                        mt: 0.5,
                      }}
                    >
                      {g.items.map(it => (
                        <Chip
                          key={it.idAssignment}
                          size='small'
                          variant='outlined'
                          clickable={!!onNavigate}
                          onClick={
                            onNavigate
                              ? () =>
                                  onNavigate({
                                    kind: 'postcode',
                                    postcode: {
                                      idPostcode: it.id,
                                      Postcode: it.postcode,
                                      City: it.city,
                                      idCountry: null,
                                    },
                                  })
                              : undefined
                          }
                          onDelete={() =>
                            void removePlzAssignment(it.idAssignment)
                          }
                          deleteIcon={
                            <Tooltip title='Zuordnung entfernen'>
                              <DeleteOutline />
                            </Tooltip>
                          }
                          label={`${it.postcode} ${it.city}`.trim()}
                        />
                      ))}
                    </Box>
                  </Box>
                ))}
              </Stack>
            )}
            {/* PLZ hinzufügen (Rolle frei wählbar) */}
            {addingPlz && (
              <Stack
                direction='row'
                spacing={1}
                alignItems='flex-start'
                sx={{ mt: 1, flexWrap: 'wrap' }}
              >
                <Box sx={{ flex: 1, minWidth: 220 }}>
                  <PostcodeSearch
                    postcodeObject={plzToAdd}
                    updatePostcodeObject={p => setPlzToAdd(p ?? null)}
                    required={false}
                  />
                </Box>
                {roleSelect(plzRole, setPlzRole)}
                <Button
                  variant='contained'
                  onClick={() => void addPlzAssignment()}
                  disabled={
                    !plzToAdd?.idPostcode || typeof plzRole !== 'number'
                  }
                >
                  Zuordnen
                </Button>
                <Button
                  onClick={() => {
                    setAddingPlz(false);
                    setPlzToAdd(null);
                    setPlzRole('');
                  }}
                >
                  Abbrechen
                </Button>
              </Stack>
            )}
          </Box>
        </CardContent>
      </Card>

      {/* Gemarkungen (nur Gericht) — zuletzt, Rolle Grundbuchamt implizit */}
      {isCourt && (
        <Card variant='outlined'>
          <CardContent>
            <Typography variant='subtitle1' fontWeight={600} sx={{ mb: 0.5 }}>
              Grundbuchamt für Gemarkungen
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {myGemarkungAssignments
                .slice()
                .sort((a, b) =>
                  gemarkungName(a.idAdministrativeUnit).localeCompare(
                    gemarkungName(b.idAdministrativeUnit)
                  )
                )
                .map(a => (
                  <Chip
                    key={a.idAssignment}
                    icon={<Apartment />}
                    size='small'
                    variant='outlined'
                    clickable={!!onNavigate}
                    onClick={
                      onNavigate
                        ? () =>
                            onNavigate({
                              kind: 'gemarkung',
                              id: a.idAdministrativeUnit,
                            })
                        : undefined
                    }
                    onDelete={() => void handleRemoveGemarkung(a)}
                    disabled={isPendingDelete}
                    deleteIcon={
                      <Tooltip title='Zuordnung entfernen'>
                        <DeleteOutline />
                      </Tooltip>
                    }
                    label={gemarkungName(a.idAdministrativeUnit)}
                  />
                ))}
              {!addingGemarkung && ownFederalState !== undefined && (
                <Chip
                  icon={<Add />}
                  size='small'
                  color='primary'
                  variant='outlined'
                  clickable
                  onClick={() => setAddingGemarkung(true)}
                  label='Gemarkung hinzufügen'
                />
              )}
            </Box>
            {myGemarkungAssignments.length === 0 && !addingGemarkung && (
              <Typography
                variant='body2'
                color='text.secondary'
                sx={{ mt: 0.5 }}
              >
                Noch keine Gemarkung zugeordnet.
              </Typography>
            )}
            {addingGemarkung && (
              <Stack
                direction='row'
                spacing={1}
                alignItems='center'
                sx={{ mt: 1 }}
              >
                <Autocomplete
                  sx={{ flex: 1 }}
                  size='small'
                  openOnFocus
                  options={addableGemarkungen}
                  value={gemarkungToAdd}
                  onChange={(_, v) => setGemarkungToAdd(v)}
                  getOptionLabel={o => o.AdministrativeUnit}
                  isOptionEqualToValue={(o, v) =>
                    o.idAdministrativeUnit === v.idAdministrativeUnit
                  }
                  filterOptions={(opts, state) => {
                    const q = state.inputValue.trim().toLowerCase();
                    if (!q) return opts.slice(0, 50);
                    return opts
                      .filter(o =>
                        o.AdministrativeUnit.toLowerCase().includes(q)
                      )
                      .slice(0, 50);
                  }}
                  renderInput={params => (
                    <TextField
                      {...params}
                      autoFocus
                      label='Gemarkung zuordnen'
                      placeholder='Gemarkung suchen…'
                    />
                  )}
                />
                <Button
                  variant='contained'
                  onClick={() => void handleAddGemarkung()}
                  disabled={
                    !gemarkungToAdd || isPendingCreateAdminUnitAssignment
                  }
                >
                  Zuordnen
                </Button>
                <Button
                  onClick={() => {
                    setAddingGemarkung(false);
                    setGemarkungToAdd(null);
                  }}
                >
                  Abbrechen
                </Button>
              </Stack>
            )}
            {ownFederalState === undefined && (
              <Alert severity='warning' sx={{ mt: 1 }}>
                Dieses Gericht hat kein Bundesland — Zuordnungen können nicht
                angelegt werden.
              </Alert>
            )}
          </CardContent>
        </Card>
      )}

      {/* Lösch-Bestätigung mit Abhängigkeits-Warnung */}
      <Dialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        maxWidth='xs'
        fullWidth
      >
        <DialogTitle>
          {isCourt ? 'Gericht löschen' : 'Behörde löschen'}
        </DialogTitle>
        <DialogContent>
          <Typography variant='body2' sx={{ mb: 1 }}>
            „{entityName}" wird <strong>deaktiviert</strong> und verschwindet
            aus allen Auswahllisten. Bereits verknüpfte Daten (Mandanten,
            Immobilien, Grundbuchblätter) bleiben erhalten; aktive Zuordnungen
            zu Gemarkungen, Gemeinden und PLZ werden mit entfernt.
          </Typography>
          {loadingDeps ? (
            <CircularProgress size={22} />
          ) : depRows.length > 0 ? (
            <Alert severity='warning' variant='outlined' sx={{ mt: 1 }}>
              Diese Stelle ist aktuell verknüpft mit:
              <Box component='ul' sx={{ m: 0.5, pl: 2 }}>
                {depRows.map(r => (
                  <li key={r.label}>
                    {r.value} × {r.label}
                  </li>
                ))}
              </Box>
              Wirklich löschen?
            </Alert>
          ) : (
            <Alert severity='info' variant='outlined' sx={{ mt: 1 }}>
              Keine aktiven Verknüpfungen gefunden.
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)}>Abbrechen</Button>
          <Button
            variant='contained'
            color='error'
            onClick={() => void handleDelete()}
            disabled={isDeleting || loadingDeps}
          >
            {isDeleting ? <CircularProgress size={18} /> : 'Löschen'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
